import { useState, useEffect } from 'react'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from './Card'
import { BarChart3, TrendingUp, Target, Activity, Calendar, CheckCircle2 } from 'lucide-react'
import { supabase } from '../services/supabase'

const CarePlanAnalytics = ({ carePlanId = null }) => {
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const organizationId = useOrganizationId()

  useEffect(() => {
    if (organizationId) {
      fetchAnalytics()
    }
  }, [organizationId, carePlanId])

  const fetchAnalytics = async () => {
    if (!organizationId) return
    try {
      setLoading(true)

      // Get care plans
      let plansQuery = supabase
        .from('care_plans')
        .select('id, status, start_date, end_date, created_at')
        .eq('organization_id', organizationId)

      if (carePlanId) {
        plansQuery = plansQuery.eq('id', carePlanId)
      }

      const { data: plans, error: plansError } = await plansQuery

      if (plansError) throw plansError

      // Get progress data
      const progressQuery = supabase
        .from('care_plan_progress')
        .select('care_plan_id, adherence_score, progress_date')
        .eq('organization_id', organizationId)

      if (carePlanId) {
        progressQuery.eq('care_plan_id', carePlanId)
      }

      const { data: progress, error: progressError } = await progressQuery

      if (progressError) throw progressError

      // Get outcomes
      const outcomesQuery = supabase
        .from('care_plan_outcomes')
        .select('care_plan_id, outcome_type')
        .eq('organization_id', organizationId)

      if (carePlanId) {
        outcomesQuery.eq('care_plan_id', carePlanId)
      }

      const { data: outcomes, error: outcomesError } = await outcomesQuery

      if (outcomesError) throw outcomesError

      // Calculate analytics
      const totalPlans = plans?.length || 0
      const activePlans = plans?.filter((p) => p.status === 'active').length || 0
      const completedPlans = plans?.filter((p) => p.status === 'completed').length || 0
      const pausedPlans = plans?.filter((p) => p.status === 'paused').length || 0

      // Calculate average adherence
      const adherenceScores = progress?.map((p) => p.adherence_score).filter((s) => s !== null) || []
      const avgAdherence =
        adherenceScores.length > 0
          ? adherenceScores.reduce((sum, score) => sum + score, 0) / adherenceScores.length
          : null

      // Calculate outcome achievement rate
      const goalAchieved = outcomes?.filter((o) => o.outcome_type === 'goal_achieved').length || 0
      const totalOutcomes = outcomes?.length || 0
      const achievementRate = totalOutcomes > 0 ? (goalAchieved / totalOutcomes) * 100 : 0

      // Calculate average duration
      const durations = plans
        ?.filter((p) => p.start_date && p.end_date)
        .map((p) => {
          const start = new Date(p.start_date)
          const end = new Date(p.end_date)
          return Math.ceil((end - start) / (1000 * 60 * 60 * 24))
        }) || []
      const avgDuration = durations.length > 0 ? durations.reduce((sum, d) => sum + d, 0) / durations.length : null

      setAnalytics({
        totalPlans,
        activePlans,
        completedPlans,
        pausedPlans,
        averageAdherence: avgAdherence ? Math.round(avgAdherence * 100) / 100 : null,
        achievementRate: Math.round(achievementRate * 100) / 100,
        averageDuration: avgDuration ? Math.round(avgDuration) : null,
        totalProgressRecords: progress?.length || 0,
        totalOutcomes,
        goalAchieved,
      })
    } catch (error) {
      console.error('Error fetching analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="text-sm text-gray-500">Loading analytics...</div>
  }

  if (!analytics) {
    return <div className="text-sm text-gray-500">No analytics data available</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <BarChart3 size={20} />
        <h3 className="text-lg font-semibold text-gray-900">Analytics</h3>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="soft" className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Plans</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.totalPlans}</p>
            </div>
            <Target size={24} className="text-primary-500" />
          </div>
        </Card>

        <Card variant="soft" className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Plans</p>
              <p className="text-2xl font-bold text-green-600">{analytics.activePlans}</p>
            </div>
            <Activity size={24} className="text-green-500" />
          </div>
        </Card>

        <Card variant="soft" className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-blue-600">{analytics.completedPlans}</p>
            </div>
            <CheckCircle2 size={24} className="text-blue-500" />
          </div>
        </Card>

        <Card variant="soft" className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Adherence</p>
              <p className="text-2xl font-bold text-purple-600">
                {analytics.averageAdherence ? `${analytics.averageAdherence}%` : 'N/A'}
              </p>
            </div>
            <TrendingUp size={24} className="text-purple-500" />
          </div>
        </Card>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card variant="soft" className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Outcome Metrics</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Achievement Rate</span>
              <span className="text-lg font-bold text-primary-600">
                {analytics.achievementRate}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Goals Achieved</span>
              <span className="text-lg font-bold text-green-600">{analytics.goalAchieved}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Outcomes</span>
              <span className="text-lg font-bold text-gray-900">{analytics.totalOutcomes}</span>
            </div>
          </div>
        </Card>

        <Card variant="soft" className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Progress Metrics</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Progress Records</span>
              <span className="text-lg font-bold text-blue-600">
                {analytics.totalProgressRecords}
              </span>
            </div>
            {analytics.averageDuration && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Avg Duration</span>
                <span className="text-lg font-bold text-gray-900">
                  {analytics.averageDuration} days
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Paused Plans</span>
              <span className="text-lg font-bold text-yellow-600">{analytics.pausedPlans}</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default CarePlanAnalytics

